import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import Select from 'components/Inputs/Select'

const linkStyle = {
  alignSelf: 'flex-start',
  padding: 0,
  border: 0,
  background: 'transparent',
  color: '#35A7FF',
  fontSize: 14,
  cursor: 'pointer'
}

const inputStyle = {
  flex: 1,
  height: 38,
  minWidth: 0,
  padding: '0 12px',
  border: 0,
  borderRadius: 15,
  boxShadow: '0px 2px 10px -8px #252525'
}

const toOption = specialty => ({ label: specialty.name, value: specialty.id })

/*
  A especialidade (Alongamento, Tradicional...) de um serviço dentro da categoria dele, como na
  planilha de metas. As especialidades são do salão: aqui se escolhe uma da categoria ou se cria uma
  na hora, sem sair do cadastro do serviço. `value` é a opção `{ label, value }` (o `label` pode faltar
  se só o id é conhecido). Trocar de categoria é com quem usa: passe `value` nulo.
*/
export default function SpecialtySelect({ categoryId, value, isDisabled = false, onChange }) {
  const businessId = AuthService.getBusinessId()

  const [options, setOptions] = useState([])
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  // Quem edita só sabe o id da especialidade: o nome vem junto com as opções da categoria.
  const selected = value
    ? (options.find(option => option.value === value.value) ?? (value.label ? value : null))
    : null

  useEffect(() => {
    let current = true

    setOptions([])
    setCreating(false)
    if (!categoryId) return undefined

    cutileiApi.get(`/businesses/${businessId}/service_specialties`, { params: { category_id: categoryId } })
      .then(({ data }) => current && setOptions(data.map(toOption)))
      .catch(logApiError)

    return () => { current = false }
  }, [categoryId])

  const handleCreate = async () => {
    if (!name.trim() || saving) return

    try {
      setSaving(true)
      setErrorMessage(null)

      const { data } = await cutileiApi.post(`/businesses/${businessId}/service_specialties`, {
        name: name.trim(),
        category_id: categoryId
      })

      setOptions(previous => [...previous, toOption(data)])
      onChange(toOption(data))
      setName('')
      setCreating(false)
    } catch (error) {
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error, 'Não foi possível criar a especialidade.'))
    } finally {
      setSaving(false)
    }
  }

  // Enter aqui cria a especialidade; sem parar o evento ele enviaria o formulário do serviço.
  const handleKeyDown = event => {
    if (event.key !== 'Enter') return

    event.preventDefault()
    event.stopPropagation()
    handleCreate()
  }

  return (
    <>
      <Select
        name='specialty'
        placeholder='Especialidade (opcional)'
        value={selected}
        options={options}
        isClearable
        isDisabled={isDisabled || !categoryId}
        onChange={(_, option) => onChange(option)}
      />
      {categoryId && !isDisabled && !creating && (
        <button type='button' style={linkStyle} onClick={() => setCreating(true)}>
          + Nova especialidade
        </button>
      )}
      {creating && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
          <input
            style={inputStyle}
            placeholder='Nome da especialidade'
            aria-label='Nome da especialidade'
            maxLength={48}
            value={name}
            onChange={event => setName(event.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button type='button' style={linkStyle} onClick={handleCreate} disabled={saving}>Criar</button>
          <button type='button' style={{ ...linkStyle, color: '#777777' }} onClick={() => setCreating(false)}>
            Cancelar
          </button>
        </div>
      )}
      {errorMessage && <p style={{ margin: 0, color: '#FF3939', fontSize: 13 }}>{errorMessage}</p>}
    </>
  )
}
